import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { SM_QUERY, useMediaQuery } from "../lib/media";
import {
  isTriggerOffscreen,
  placePopover,
  type Align as PopoverAlign,
  type Placement,
} from "../lib/popover-position";

// Kleine, konsistente UI-Bausteine — bewusst keine Component-Library:
// vier Primitives reichen für dieses Tool und bleiben vollständig unter Kontrolle.

// Padding bewusst NICHT in der Basis: ein px-0 im className kann px-3.5 nicht
// überschreiben (CSS-Reihenfolge der Utilities, nicht Klassen-Reihenfolge,
// entscheidet) — Icon-Buttons laufen deshalb über die square-Prop.
const buttonBase =
  "inline-flex h-9 items-center justify-center gap-2 rounded-lg text-sm font-medium " +
  "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 " +
  "disabled:pointer-events-none disabled:opacity-50";

const buttonVariants = {
  primary: "bg-accent text-white shadow-sm hover:bg-accent-hover",
  ghost:
    "border border-zinc-200 text-zinc-700 hover:bg-zinc-100 " +
    "dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800",
  danger:
    "border border-red-200 text-red-600 hover:bg-red-50 " +
    "dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-950/40",
} as const;

type ButtonStyleProps = {
  variant?: keyof typeof buttonVariants;
  /** Quadratischer Icon-Button (36×36) ohne Innenabstand */
  square?: boolean;
};

export function Button({
  variant = "primary",
  square = false,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & ButtonStyleProps) {
  return (
    <button
      className={`${buttonBase} ${square ? "w-9" : "px-3.5"} ${buttonVariants[variant]} ${className}`}
      {...props}
    />
  );
}

/** Link im Button-Gewand — für echte Navigationsziele (neuer Tab, kopierbare URL). */
export function ButtonLink({
  variant = "primary",
  square = false,
  className = "",
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & ButtonStyleProps) {
  return (
    <a
      className={`${buttonBase} ${square ? "w-9" : "px-3.5"} ${buttonVariants[variant]} ${className}`}
      {...props}
    />
  );
}

/** Zeile in einem Popover — auf Touch bewusst 44 px hoch, am Desktop kompakter. */
export const popoverItem =
  "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-3 text-left text-sm " +
  "transition-colors hover:bg-zinc-100 sm:py-2 dark:hover:bg-zinc-800";

/** Überschrift über einer Gruppe von Popover-Zeilen. */
export const popoverLabel =
  "px-2.5 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400";

/**
 * Aufklapp-Panel an einem Auslöser — die gemeinsame Mechanik hinter Konto-
 * Umschalter, Benutzermenü und Überlaufmenü. Übernimmt Öffnen/Schließen,
 * Platzierung und Tastatur/Touch-Bedienung; die Aufrufer liefern nur Inhalt.
 *
 * Ab `sm` ein schwebendes Panel per Portal (fixed): overflow-Container wie die
 * scrollbare Szenenliste im Editor können es so nicht abschneiden. Darunter ein
 * Bottom-Sheet — das ist auf dem Daumen erreichbar und braucht keine
 * Positionsmathematik am Viewport-Rand.
 */
export function Popover({
  trigger,
  triggerClassName = "",
  title,
  role = "menu",
  align = "end",
  width = "w-56",
  sheetTitle,
  children,
}: {
  /** Inhalt des Auslöse-Buttons; bekommt den Offen-Zustand (z. B. für Pfeile) */
  trigger: (state: { open: boolean }) => ReactNode;
  triggerClassName?: string;
  title?: string;
  role?: "menu" | "listbox";
  /** An welcher Kante des Auslösers das Panel hängt (nur Desktop) */
  align?: PopoverAlign;
  /** Panelbreite am Desktop; im Bottom-Sheet gilt sie nicht */
  width?: string;
  /** Überschrift im Bottom-Sheet — auf dem Handy fehlt sonst der Bezug */
  sheetTitle?: string;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Placement | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery(SM_QUERY);
  const close = useCallback(() => setOpen(false), []);

  // Platzierung erst NACH dem Rendern: vorher ist die Panelgröße unbekannt.
  // useLayoutEffect = vor dem Paint, das versteckte Zwischenbild sieht niemand.
  useLayoutEffect(() => {
    if (!open || !isDesktop) return;

    const place = () => {
      const btn = btnRef.current;
      const panel = panelRef.current;
      if (!btn || !panel) return;
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      const r = btn.getBoundingClientRect();
      if (isTriggerOffscreen(r, viewport)) {
        setOpen(false);
        return;
      }
      setPos(
        placePopover(r, { width: panel.offsetWidth, height: panel.offsetHeight }, viewport, align),
      );
    };

    place();
    // Neu berechnen statt schließen: auf Mobilgeräten feuert resize schon beim
    // Ein- und Ausfahren der URL-Leiste — ein Menü, das dabei zuklappt, wirkt kaputt.
    let frame = 0;
    const onMove = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, isDesktop, align]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);

    // Im Sheet-Modus schließt der Backdrop selbst (onClick weiter unten). Würde
    // hier stattdessen pointerdown schließen, wäre der Backdrop beim Verschicken
    // des Klicks schon aus dem DOM — Touch-Geräte bestimmen das Klickziel erst
    // dann per Hit-Test und träfen das Element DARUNTER („Ghost Click“).
    if (!isDesktop) return () => document.removeEventListener("keydown", onKey);

    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!btnRef.current?.contains(target) && !panelRef.current?.contains(target)) setOpen(false);
    };
    // pointerdown deckt Maus, Touch und Stift gemeinsam ab — mousedown allein
    // ist auf Touch-Geräten unzuverlässig.
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, isDesktop]);

  // Sheet offen → Seite dahinter festhalten. Der Backdrop fängt mit touch-none
  // schon jede Wischgeste ab; overflow:hidden deckt zusätzlich Mausrad und
  // Trackpad im schmalen Desktop-Fenster ab.
  useEffect(() => {
    if (!open || isDesktop) return;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    // Verschwindende Scrollleiste würde das Layout sonst springen lassen. Die
    // Breite wird davor UND danach gemessen: ist die Rinne ohnehin reserviert
    // (scrollbar-gutter, siehe styles.css), ändert sich nichts und es braucht
    // keinen Ausgleich.
    const widthBefore = document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    const gap = document.documentElement.clientWidth - widthBefore;
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
    };
  }, [open, isDesktop]);

  const surface =
    "overscroll-contain border-zinc-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900";

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        title={title}
        aria-haspopup={role}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={triggerClassName}
      >
        {trigger({ open })}
      </button>
      {open &&
        createPortal(
          isDesktop ? (
            <div
              ref={panelRef}
              role={role}
              style={
                pos
                  ? { top: pos.top, bottom: pos.bottom, left: pos.left, maxHeight: pos.maxHeight }
                  : { top: 0, left: 0, visibility: "hidden" }
              }
              className={`popover-panel scrollbar-slim fixed z-40 overflow-y-auto rounded-xl border p-1.5 ${width} ${surface}`}
            >
              {children(close)}
            </div>
          ) : (
            <>
              {/* Schließt auf click, nicht auf pointerdown: nur so liegt der
                  Backdrop beim Klick noch da und schluckt ihn, statt ihn an
                  die Seite darunter durchzureichen. */}
              <div
                onClick={close}
                className="fixed inset-0 z-40 touch-none bg-black/40 backdrop-blur-[1px]"
                aria-hidden
              />
              <div
                ref={panelRef}
                role={role}
                className={`popover-sheet fixed inset-x-0 bottom-0 z-40 max-h-[80dvh] overflow-y-auto rounded-t-2xl border-t p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] ${surface}`}
              >
                <span
                  aria-hidden
                  className="mx-auto mb-1 block h-1 w-9 rounded-full bg-zinc-300 dark:bg-zinc-700"
                />
                {sheetTitle && <p className={popoverLabel}>{sheetTitle}</p>}
                {children(close)}
              </div>
            </>
          ),
          document.body,
        )}
    </>
  );
}

export type DropMenuItem =
  { label: string; icon?: ReactNode; onClick: () => void; danger?: boolean } | "divider";

/** Überlauf-Menü (⋯) — eigene Komponente, bewusst kein natives Kontextmenü. */
export function DropMenu({
  button,
  buttonClassName = "",
  plain = false,
  title,
  items,
}: {
  button: ReactNode;
  buttonClassName?: string;
  /** true: kein Button-Rahmen — buttonClassName bestimmt das Aussehen komplett */
  plain?: boolean;
  title?: string;
  items: DropMenuItem[];
}) {
  return (
    <Popover
      title={title}
      sheetTitle={title}
      align="end"
      width="w-56"
      triggerClassName={
        plain ? buttonClassName : `${buttonBase} w-9 ${buttonVariants.ghost} ${buttonClassName}`
      }
      trigger={() => button}
    >
      {(close) =>
        items.map((item, i) =>
          item === "divider" ? (
            <div key={i} className="my-1 border-t border-zinc-100 dark:border-zinc-800" />
          ) : (
            <button
              key={i}
              role="menuitem"
              onClick={() => {
                close();
                item.onClick();
              }}
              className={`${popoverItem} ${
                item.danger
                  ? "text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                  : "text-zinc-700 dark:text-zinc-300"
              }`}
            >
              {item.icon}
              {item.label}
            </button>
          ),
        )
      }
    </Popover>
  );
}

const fieldBase =
  "h-9 rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-900 " +
  "placeholder:text-zinc-400 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30 " +
  "dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500";

export function Input({
  className = "",
  selectOnFocus = false,
  onFocus,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  /** Inhalt beim Fokussieren markieren — für „Umbenennen“-Felder, wo man meist alles ersetzt */
  selectOnFocus?: boolean;
}) {
  return (
    <input
      className={`${fieldBase} ${className}`}
      onFocus={(e) => {
        if (selectOnFocus) e.currentTarget.select();
        onFocus?.(e);
      }}
      {...props}
    />
  );
}

export function Select({ className = "", ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`${fieldBase} ${className}`} {...props} />;
}

/** In-App-Dialog — wir nutzen bewusst NIE window.prompt/confirm/alert. */
export function Modal({
  open,
  onClose,
  title,
  children,
  wide = false,
  showClose = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  wide?: boolean;
  /** X oben rechts — für Dialoge ohne eigene Abbrechen-/Schließen-Buttons */
  showClose?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  // Portal auf <body>: fixed bräche sonst, sobald ein Vorfahr einen Containing
  // Block bildet (z. B. backdrop-blur der Nav beim Konto-Dialog im UserMenu).
  return createPortal(
    <div
      className="modal-overlay fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={`modal-panel w-full ${wide ? "max-w-lg" : "max-w-sm"} rounded-2xl border border-zinc-200 bg-white p-5 shadow-xl dark:border-zinc-700/60 dark:bg-zinc-900`}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h3 className="font-semibold">{title}</h3>
          {showClose && (
            <button
              onClick={onClose}
              className="-m-1 rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function Card({
  title,
  children,
  className = "",
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900 ${className}`}
    >
      {title && <h3 className="mb-1 text-sm font-semibold">{title}</h3>}
      {children}
    </section>
  );
}

/**
 * Segmentierte Auswahl — wenige, kurze Werte nebeneinander statt in einer
 * Aufklappliste: die Möglichkeiten stehen offen da und sind ein Klick weit weg.
 *
 * Darunter liegen echte Radio-Buttons (nur optisch versteckt): Tastatur,
 * Screenreader und Formular-Semantik kommen so vom Browser, nur das Aussehen
 * ist unseres.
 */
export function SegmentedControl<T extends string>({
  name,
  value,
  options,
  onChange,
  label,
}: {
  /** Gruppenname der Radio-Buttons — muss im Formular eindeutig sein */
  name: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  /** Beschriftung der Gruppe für Screenreader (steht sichtbar in der Zeile) */
  label?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex rounded-lg border border-zinc-200 bg-zinc-100/70 p-0.5 dark:border-zinc-700 dark:bg-zinc-800/60"
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <label
            key={o.value}
            className={`rounded-md px-2.5 py-1 text-sm transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/40 ${
              active
                ? "bg-white font-medium text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-50"
                : "text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
            }`}
          >
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={active}
              onChange={() => onChange(o.value)}
              className="sr-only"
            />
            {o.label}
          </label>
        );
      })}
    </div>
  );
}

/** An/Aus-Schalter — für Einstellungen, die sofort nur zwei Zustände kennen. */
export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Beschriftung für Screenreader (steht sichtbar in der Zeile) */
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`inline-flex h-6 w-10 shrink-0 items-center rounded-full p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${
        checked ? "bg-accent" : "bg-zinc-300 dark:bg-zinc-700"
      }`}
    >
      {/* Bewusst die Standard-`transition`: Tailwind v4 setzt translate-* als
          eigene CSS-Eigenschaft, die eine reine transform-Transition nicht sieht. */}
      <span
        className={`size-5 rounded-full bg-white shadow-sm transition ${checked ? "translate-x-4" : ""}`}
      />
    </button>
  );
}
